import { useParams, Link } from "react-router-dom";
import { getGuideBySlug } from "../content/marketplace-content-loader";
import type { Preview } from "../content/marketplace-content-loader";
import { SIGNUP_FORM_URL } from "../external-links";

/* ────── Utility Icons ────── */

function DownloadIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 2v8M5 7.5L8 10.5l3-3" />
      <path d="M3 13h10" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 12 12" fill="#C4993C" stroke="none">
      <path d="M6 .8l1.5 3 3.3.5-2.4 2.3.6 3.4L6 8.2 3 10l.6-3.4L1.2 4.3l3.3-.5z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="#248a3d"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 8.5l3.5 3.5L13 5" />
    </svg>
  );
}

/* ────── Preview Mockup Cards ────── */

function PreviewMetric({ preview }: { preview: Preview }) {
  return (
    <div className="min-w-[160px] flex-1 rounded-2xl bg-kin-cream/80 p-4">
      <span className="font-serif text-xs font-bold text-kin-black">Kin</span>
      <p className="mt-2 text-xs font-medium text-kin-muted">{preview.title}</p>
      <p className="mt-1 text-2xl font-bold text-kin-black">{preview.value}</p>
      {preview.unit && <p className="text-xs text-kin-muted">{preview.unit}</p>}
      <svg className="mt-3 h-8 w-full" viewBox="0 0 160 40" fill="none">
        <path
          d="M0 35 Q20 30, 40 28 T80 18 T120 12 T160 8"
          stroke="#34c759"
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.7"
        />
        <path d="M0 35 Q20 30, 40 28 T80 18 T120 12 T160 8 V40 H0 Z" fill="#34c759" opacity="0.1" />
      </svg>
    </div>
  );
}

function PreviewChecklist({ preview }: { preview: Preview }) {
  return (
    <div className="min-w-[160px] flex-1 rounded-2xl bg-kin-cream/80 p-4">
      <span className="font-serif text-xs font-bold text-kin-black">Kin</span>
      <p className="mt-2 text-xs font-medium text-kin-muted">{preview.title}</p>
      <div className="mt-3 space-y-2">
        {[80, 65, 72].map((w, i) => (
          <div key={i} className="flex items-center gap-2">
            <div
              className={`h-4 w-4 flex-shrink-0 rounded-sm border ${i < 2 ? "border-kin-green bg-kin-green/20" : "border-kin-olive/30"}`}
            />
            <div className="h-2 rounded-full bg-kin-olive/20" style={{ width: `${w}%` }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function PreviewChart({ preview }: { preview: Preview }) {
  const heights = [30, 20, 10, 15, 25, 18, 22];
  return (
    <div className="min-w-[160px] flex-1 rounded-2xl bg-kin-cream/80 p-4">
      <div className="flex items-center">
        <span className="font-serif text-xs font-bold text-kin-black">Kin</span>
        <span className="ml-auto text-[10px] text-kin-muted">Biomarker Analysis</span>
      </div>
      <p className="mt-1 text-xs font-medium text-kin-muted">{preview.subtitle}</p>
      <p className="mt-1 text-2xl font-bold text-kin-black">{preview.value}</p>
      {preview.unit && <p className="text-xs text-kin-muted">{preview.unit}</p>}
      <svg className="mt-3 h-10 w-full" viewBox="0 0 140 50" fill="none">
        {heights.map((y, i) => (
          <rect
            key={i}
            x={5 + i * 20}
            y={y}
            width="14"
            height={50 - y}
            rx="3"
            fill="#34c759"
            opacity={0.3 + i * 0.05}
          />
        ))}
      </svg>
    </div>
  );
}

function PreviewCard({ preview }: { preview: Preview }) {
  switch (preview.type) {
    case "metric":
      return <PreviewMetric preview={preview} />;
    case "checklist":
      return <PreviewChecklist preview={preview} />;
    case "chart":
      return <PreviewChart preview={preview} />;
  }
}

/* ────── Page Component ────── */

export function GuideDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const guide = slug ? getGuideBySlug(slug) : undefined;

  if (!guide) {
    return (
      <div className="px-6 py-20 text-center">
        <h1 className="font-serif text-3xl font-bold text-kin-black">Guide not found</h1>
        <p className="mt-3 text-kin-body/80">
          The guide you&rsquo;re looking for doesn&rsquo;t exist.
        </p>
        <Link
          to="/marketplace"
          className="mt-6 inline-block text-sm font-medium text-kin-green-dark underline underline-offset-2"
        >
          Back to Marketplace
        </Link>
      </div>
    );
  }

  return (
    <div className="px-6 pb-16 pt-10 sm:px-8 sm:pb-20 sm:pt-14">
      <div className="mx-auto max-w-6xl">
        {/* Hero */}
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <h1 className="font-serif text-4xl font-bold leading-tight text-kin-black sm:text-5xl md:text-6xl">
              {guide.name}.
            </h1>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-kin-body/80">
              {guide.subtitle}
            </p>
          </div>
          <div className="flex flex-shrink-0 flex-wrap items-center gap-4 sm:flex-col sm:items-end">
            <a
              href={SIGNUP_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-11 items-center rounded-full px-6 text-sm font-semibold text-white transition hover:opacity-90 active:scale-[0.98]"
              style={{ background: guide.buttonBg }}
            >
              Install Guide
            </a>
            <div className="flex items-center gap-1.5 text-kin-muted">
              <span className="inline-block h-4 w-4">
                <DownloadIcon />
              </span>
              <span className="text-sm">
                <span className="font-semibold text-kin-black">{guide.installs}</span> installs
              </span>
            </div>
          </div>
        </div>

        {/* Bento Grid */}
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* About — spans 2 cols on lg */}
          <div className="kin-surface rounded-3xl p-6 lg:col-span-2">
            <h2 className="text-lg font-semibold text-kin-black">About this Guide</h2>
            <div className="mt-3 space-y-3">
              {guide.about.map((p, i) => (
                <p key={i} className="text-sm leading-relaxed text-kin-body/80">
                  {p}
                </p>
              ))}
            </div>
            {guide.capabilities.length > 0 && (
              <>
                <h3 className="mt-6 text-xs font-semibold uppercase tracking-[0.14em] text-kin-muted">
                  What You Can Do
                </h3>
                <div className="mt-3 space-y-3">
                  {guide.capabilities.map((cap) => (
                    <div key={cap.title} className="flex items-start gap-3">
                      <span
                        className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-xl text-sm"
                        style={{ background: cap.color }}
                      >
                        {cap.icon}
                      </span>
                      <p className="text-sm leading-snug text-kin-body/80">
                        <span className="font-medium text-kin-black">{cap.title}:</span> {cap.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Creator */}
          <div className="kin-surface rounded-3xl p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-kin-muted">
              Created By
            </p>
            {guide.creator.verified && (
              <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-kin-green/10 px-3 py-1 text-xs font-medium text-kin-green-dark">
                <span className="inline-block h-3.5 w-3.5">
                  <CheckIcon />
                </span>
                Verified Creator
              </span>
            )}
            <h3 className="mt-3 font-serif text-2xl font-bold text-kin-black">
              {guide.creator.name}
            </h3>
            {guide.creator.desc && (
              <p className="mt-2 text-sm leading-relaxed text-kin-body/80">{guide.creator.desc}</p>
            )}
          </div>

          {/* Preview */}
          {guide.previews.length > 0 && (
            <div className="kin-surface rounded-3xl p-6 md:col-span-2">
              <h2 className="text-lg font-semibold text-kin-black">Guide Preview</h2>
              <div className="mt-4 flex flex-wrap gap-4">
                {guide.previews.map((preview) => (
                  <PreviewCard key={preview.title} preview={preview} />
                ))}
              </div>
            </div>
          )}

          {/* Reviews */}
          {guide.reviews.length > 0 && (
            <div className="kin-surface rounded-3xl p-6">
              <h2 className="text-lg font-semibold text-kin-black">Ratings &amp; Reviews</h2>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-serif text-4xl font-bold text-kin-black">
                  {guide.ratingNum}
                </span>
                <span className="text-sm text-kin-muted">/5</span>
                <div className="ml-2 flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i} className="inline-block h-3 w-3">
                      <StarIcon />
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-4 space-y-3">
                {guide.reviews.map((review) => (
                  <div key={review.quote} className="rounded-2xl bg-kin-cream/60 p-4">
                    <p className="text-sm italic leading-relaxed text-kin-body">
                      &ldquo;{review.quote}&rdquo;
                    </p>
                    <p className="mt-1.5 text-xs text-kin-muted">{review.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
